/* eslint-disable complexity -- complex code */
import { bemClassBuilder } from '@vista-digital/bem-class-builder'
import { deviceHasTouch } from '@vista-digital/occ-ui/src/lib/utils/device-has-touch'
import type { EmblaCarouselType, EmblaPluginType } from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import ClassNames from 'embla-carousel-class-names'
import useEmblaCarousel from 'embla-carousel-react'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { observer } from 'mobx-react'
import { useCallback, useEffect, useMemo } from 'react'
import { useCarouselControls } from './hooks'
import { CarouselAutoPlayToggleButton, CarouselPositionIndicator, CarouselScrollButton } from './subcomponents'
import type { CarouselProps } from './types/props'

const b = bemClassBuilder('v-carousel')

export const OccCarousel: React.FC<CarouselProps> = observer(props => {
    const { config, disabled, children } = props
    const { settings, touchSettings, showPositionIndicators, scrollToSlide, autoPlay } = config

    const carouselSettings = useMemo(() => {
        const hasTouch = deviceHasTouch()
        if (!hasTouch) {
            return settings
        }
        return { ...settings, ...touchSettings }
    }, [settings, touchSettings])

    const slidesToScroll = carouselSettings?.slidesToScroll ?? 'auto'

    const plugins: EmblaPluginType[] = [ClassNames({ snapped: 'is-snapped', inView: '' }), WheelGesturesPlugin()]

    if (autoPlay) {
        plugins.push(Autoplay({ playOnInit: autoPlay.enabled, delay: autoPlay.delay }))
    }

    const [emblaRef, emblaApi] = useEmblaCarousel(
        {
            ...carouselSettings,
            slidesToScroll,
            active: disabled !== true,
            align: carouselSettings?.align ?? 'start',
            containScroll: 'trimSnaps',
            watchSlides: api => {
                api.scrollTo(0, true)
                return true
            },
        },
        plugins
    )

    const {
        selectedIndex,
        slideCount,
        scrollTo,
        scrollPrevious,
        scrollNext,
        scrollPreviousDisabled,
        scrollNextDisabled,
        toggleAutoPlay,
        isAutoPlaying,
    } = useCarouselControls({ emblaApi, config })

    const onInit = useCallback(
        (api: EmblaCarouselType, targetSlideIndex?: number) => {
            config.onInit?.(api)
            // Set slidesToScroll to 1 when displaying a single slide.
            if (slidesToScroll === 'auto' && api.slideNodes().length === 1) {
                api.reInit({ slidesToScroll: 1 })
            }

            if (targetSlideIndex) {
                const { slideRegistry } = api.internalEngine()
                const targetSnap = slideRegistry.findIndex(group => group.includes(targetSlideIndex))
                if (targetSnap >= 0) {
                    api.scrollTo(targetSnap)
                }
            }
        },
        [config, slidesToScroll]
    )

    useEffect(() => {
        if (!emblaApi) {
            return
        }

        onInit(emblaApi, scrollToSlide)

        emblaApi.on('resize', api => onInit(api, scrollToSlide))
    }, [emblaApi, onInit, carouselSettings, scrollToSlide, config])

    const showScrollButtons = !disabled && !deviceHasTouch()
    const autoPlayEnabled = autoPlay?.enabled ?? false
    const showAutoPlayControls = autoPlayEnabled && autoPlay?.showToggle

    return (
        <div className={b.block()}>
            {showScrollButtons && (
                <CarouselScrollButton disabled={scrollPreviousDisabled} onClick={scrollPrevious} direction="previous" />
            )}
            <div className={b.element('viewport', { showScrollButtons })} ref={emblaRef}>
                <ul className={b.element('container')}>{children}</ul>
            </div>
            {showScrollButtons && (
                <CarouselScrollButton disabled={scrollNextDisabled} onClick={scrollNext} direction="next" />
            )}

            {showPositionIndicators && (
                <div className={b.element('position-indicators-wrapper')} role="tablist">
                    {showAutoPlayControls && (
                        <CarouselAutoPlayToggleButton onClick={toggleAutoPlay} isPlaying={isAutoPlaying} />
                    )}
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment --- needed */}
                    {[...Array(slideCount)].map((_, index) => (
                        <CarouselPositionIndicator
                            key={index}
                            index={index}
                            selected={index === selectedIndex}
                            onClick={() => scrollTo(index)}
                            autoPlay={{
                                enabled: autoPlayEnabled,
                                delay: autoPlay?.delay,
                                isPlaying: isAutoPlaying,
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    )
})
OccCarousel.displayName = 'OccCarousel'
